import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  covaListCustomers,
  greenlineListCustomers,
} from '../../../../gateways/breadstack/customer'
import { usePantryStore } from '../../../../providers/PantryProvider'

export interface CouponCustomerResult {
  id: string
  name: string
  email: string
}

// Store types that expose a customer-list gateway in pantry-features. Others
// (shopify / woocommerce / flowhub / lightspeed) have no customer search here,
// so the picker degrades to free-email entry only.
const SEARCH_SUPPORTED_STORE_TYPES = ['cova', 'greenline'] as const

type SearchSupportedStoreType = (typeof SEARCH_SUPPORTED_STORE_TYPES)[number]

const isSearchSupported = (
  storeType: string | undefined,
): storeType is SearchSupportedStoreType =>
  Boolean(storeType) &&
  SEARCH_SUPPORTED_STORE_TYPES.includes(storeType as SearchSupportedStoreType)

interface UseCouponCustomerSearchOptions {
  // Emails already picked as chips — filtered out of the dropdown so the same
  // customer can't be added twice. Compared case-insensitively.
  excludeEmails?: readonly string[]
}

/**
 * Debounced customer search powering the "Specific customers" mode of the
 * coupon modal's Customer eligibility section. Consumes the Customer module's
 * per-POS list gateway (`covaListCustomers` / `greenlineListCustomers`) — a
 * read-only cross-module consumption, mirroring how `useCouponProductSearch`
 * consumes the Product gateway. Returns `searchSupported: false` (and an empty
 * result list) for store types without a customer-list endpoint, in which case
 * the picker still allows typing a free email.
 */
export const useCouponCustomerSearch = (
  query: string,
  options: UseCouponCustomerSearchOptions = {},
): {
  results: CouponCustomerResult[]
  isLoading: boolean
  searchSupported: boolean
} => {
  const store = usePantryStore()
  const storeId = store?.storeId ?? ''
  const storeType = store?.storeType
  const trimmed = query.trim()
  const supported = isSearchSupported(storeType)
  const { excludeEmails } = options

  const { data, isFetching } = useQuery({
    queryKey: [
      'loyalty',
      'coupons',
      'customer-search',
      storeType,
      storeId,
      trimmed,
    ],
    queryFn: async () => {
      const response = supported
        ? storeType === 'cova'
          ? await covaListCustomers(storeId, {
              page: 1,
              limit: 20,
              search: trimmed,
            })
          : await greenlineListCustomers(storeId, {
              page: 1,
              limit: 20,
              search: trimmed,
            })
        : { items: [] }
      return response.items
        .map<CouponCustomerResult>((c) => ({
          id: c.id,
          name: c.fullName || `${c.firstName} ${c.lastName}`.trim(),
          email: c.email,
        }))
        .filter((c) => c.email.length > 0)
    },
    enabled: Boolean(storeId) && supported && trimmed.length > 0,
    staleTime: 30 * 1000,
  })

  // Exclude already-picked emails (case-insensitive). Rebuilt each render via
  // `useMemo` so the filter reflects the latest pick state without reallocating
  // on every keystroke.
  const excludeSet = useMemo(
    () => new Set((excludeEmails ?? []).map((e) => e.toLowerCase())),
    [excludeEmails],
  )
  const results = useMemo(
    () => (data ?? []).filter((r) => !excludeSet.has(r.email.toLowerCase())),
    [data, excludeSet],
  )

  return { results, isLoading: isFetching, searchSupported: supported }
}
